import React, { useState, useEffect } from 'react';
import './CreateAuthors.css';
import './HomeAdmin.css';
import AdminNavbar from './AdminNavbar';

const API_URL = 'https://backend-zonamicroondas.vercel.app/api';
const PAGE_SIZE = 10;

const generateSlug = (text = '') =>
  text
    .toLowerCase()
    .trim()
    .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i')
    .replace(/ó/g, 'o').replace(/ú/g, 'u').replace(/ñ/g, 'n')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export default function CreateTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({ name: '', slug: '' });
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/tags`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const sorted = data.success ? [...data.data].sort((a, b) => a.id - b.id) : [];
      setTags(sorted);
    } catch (err) {
      setError('Error al cargar tags: ' + err.message);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name' && !editingId) {
      setFormData(prev => ({ ...prev, name: value, slug: generateSlug(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        slug: formData.slug.trim() || generateSlug(formData.name)
      };

      const url = editingId ? `${API_URL}/tags/${editingId}` : `${API_URL}/tags`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || `HTTP error ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Operación fallida');

      setSuccess(editingId ? 'Tag actualizado exitosamente' : 'Tag creado exitosamente');
      setFormData({ name: '', slug: '' });
      setEditingId(null);
      setShowForm(false);
      await loadTags();
      setPage(0);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(err.message || 'Error al guardar tag');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tag) => {
    setEditingId(tag.id);
    setShowForm(true);
    setFormData({ name: tag.name || '', slug: tag.slug || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowForm(false);
    setFormData({ name: '', slug: '' });
    setError(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este tag? Se quitará de todas las noticias asociadas.')) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/tags/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || `HTTP error ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al eliminar');
      setSuccess('Tag eliminado');
      await loadTags();
      setPage(0);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.message || 'Error al eliminar tag');
    } finally {
      setLoading(false);
    }
  };

  const filtered = search.trim()
    ? tags.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.slug.toLowerCase().includes(search.toLowerCase())
      )
    : tags;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + PAGE_SIZE, filtered.length);

  return (
    <div className="home-container">
      <AdminNavbar />

      <main className="main-content">
        <div className="create-authors">
          {(success || error) && !showForm && (
            <>
              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}
            </>
          )}

          {showForm && (
            <>
              <div className="create-authors-header">
                <h2>{editingId ? 'Editar Tag' : 'Crear Tag'}</h2>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <form className="author-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre *</label>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Nombre del tag"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Slug (editable)</label>
                    <input
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      placeholder="slug-del-tag"
                    />
                    <small>Se genera automáticamente desde el nombre.</small>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Guardando...' : editingId ? 'Actualizar Tag' : 'Crear Tag'}
                  </button>
                </div>
              </form>
              <hr />
            </>
          )}

          <div className="authors-list">
            <div className="list-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <h3>Tags Registrados ({tags.length})</h3>
              {!showForm && (
                <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditingId(null); setFormData({ name: '', slug: '' }); }}>
                  Crear Nuevo Tag
                </button>
              )}
            </div>

            <div style={{ margin: '12px 0' }}>
              <input
                type="text"
                placeholder="Buscar por nombre o slug..."
                value={search}
                onChange={handleSearchChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {loading && tags.length === 0 ? (
              <div className="loading">Cargando tags...</div>
            ) : tags.length === 0 ? (
              <div className="no-data">No hay tags registrados</div>
            ) : filtered.length === 0 ? (
              <div className="no-data">No hay tags que coincidan con "{search}"</div>
            ) : (
              <>
                <table className="authors-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Slug</th>
                      <th className="centered">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map(tag => (
                      <tr key={tag.id}>
                        <td>{tag.id}</td>
                        <td>{tag.name}</td>
                        <td>{tag.slug}</td>
                        <td className="centered">
                          <button className="btn btn-secondary" onClick={() => handleEdit(tag)}>Editar</button>
                          <button className="btn btn-danger" onClick={() => handleDelete(tag.id)} style={{ marginLeft: 8 }}>
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filtered.length > PAGE_SIZE && (
                  <div className="pagination" style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                    <button className="page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>&lt;</button>
                    <div className="page-info" style={{ minWidth: 220, textAlign: 'center' }}>
                      Tags {rangeStart}-{rangeEnd} de {filtered.length} (página {page + 1} de {totalPages})
                    </div>
                    <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>&gt;</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-links">
          <a href="#privacidad">Política de Privacidad</a>
          <a href="#terminos">Términos y Condiciones</a>
          <a href="#contacto">Contacto</a>
        </div>
        <p className="footer-text">© 2025 Panel de Administrador. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
